/**
 * 合同会社eon トップページ用スクリプト
 *
 * 概要:
 * - demo/Scroll-Transition-main（Random Grid）を参考に、スクロール連動のセクション切替トランジションを実装
 * - demo/GooeyCursor-main（index.html）を参考に、Gooey Cursor を実装（マウス等のファイン入力かつ hover 可能な環境のみ）
 * - 1ページ構成のためグローバルナビは出力しない（header.php 参照）
 *
 * 制限事項:
 * - eon-front ページでのみ動作する
 * - Gooey Cursor はタッチ主体端末では初期化しない（pointer / hover メディアで判定）
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { PowerGlitch } from 'powerglitch';

/**
 * 定数
 */
const EON_GRID_ROWS = 0;
const EON_GRID_STAGGER_EACH = 0.008;
const EON_GLITCH_INTERVAL_MS_MIN = 2800;
const EON_GLITCH_INTERVAL_MS_MAX = 5200;
const EON_GLITCH_BURST_MS_MIN = 220;
const EON_GLITCH_BURST_MS_MAX = 360;

// フォント比較UI（クライアント確認用）は削除済み

gsap.registerPlugin(ScrollTrigger);

/**
 * ScrollTrigger の marker 表示を有効にするか
 *
 * 仕様:
 * - URL に `?stMarkers=1` が付いている場合のみ marker を表示する
 *
 * @returns {boolean}
 */
function shouldShowScrollMarkers() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('stMarkers') === '1';
  } catch {
    return false;
  }
}

/**
 * ScrollTrigger marker の表示上限（スライド単位）
 *
 * 仕様:
 * - `?stMarkersLimit=2` のように指定できる
 * - 未指定時は 2（デバッグ用途で最小限）
 * - 1未満や不正値は 2 にフォールバック
 *
 * @returns {number}
 */
function getScrollMarkersLimit() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('stMarkersLimit');
    if (!raw) return 2;

    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1) return 2;
    return Math.floor(n);
  } catch {
    return 2;
  }
}

/**
 * Lenis（スムーススクロール）初期化
 */
function initLenis() {
  const isTouch = window.matchMedia('(pointer: coarse)').matches;
  const lenis = new Lenis({
    lerp: 0.15,
    smoothWheel: true,
    smoothTouch: !isTouch,
  });

  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  return lenis;
}

/**
 * RandomGrid: 列数（レスポンシブ）
 *
 * @returns {number}
 */
function getGridCols() {
  // Bootstrap のブレークポイント（md=768px, lg=992px）に合わせる
  if (window.innerWidth <= 767) return 6;
  if (window.innerWidth <= 991) return 10;
  return 14;
}

/**
 * RandomGrid: SVG mask 用のセル生成
 *
 * @param {string} groupId
 * @returns {SVGRectElement[]|null}
 */
function createGridCells(groupId) {
  const g = document.getElementById(groupId);
  if (!g) return null;

  g.innerHTML = '';

  const width = window.innerWidth;
  const height = window.innerHeight;
  const vbWidth = 100;
  const vbHeight = (height / width) * 100;

  const cols = getGridCols();
  const rows = EON_GRID_ROWS || Math.round(cols * (vbHeight / vbWidth));

  const cellW = vbWidth / cols;
  const cellH = vbHeight / rows;

  const svgNS = 'http://www.w3.org/2000/svg';
  const cells = [];

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const rect = document.createElementNS(svgNS, 'rect');
      rect.setAttribute('x', String(x * cellW));
      rect.setAttribute('y', String(y * cellH));
      rect.setAttribute('width', String(cellW));
      rect.setAttribute('height', String(cellH));
      rect.setAttribute('fill', 'white');
      rect.setAttribute('shape-rendering', 'crispEdges');
      rect.setAttribute('opacity', '0');
      g.appendChild(rect);
      cells.push(rect);
    }
  }

  return cells;
}

/**
 * RandomGrid: レイアウト更新（viewBox, mask rect, image size, cells生成）
 *
 * @returns {SVGRectElement[][]}
 */
function buildTransitionMasks() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const vbWidth = 100;
  const vbHeight = (height / width) * 100;

  const layers = document.querySelectorAll('.eon-transition-layer');
  const cellSets = [];

  layers.forEach((svg) => {
    svg.setAttribute('viewBox', `0 0 ${vbWidth} ${vbHeight}`);

    const maskRect = svg.querySelector('mask rect');
    if (maskRect) {
      maskRect.setAttribute('width', String(vbWidth));
      maskRect.setAttribute('height', String(vbHeight));
    }

    const img = svg.querySelector('image');
    if (img) {
      img.setAttribute('width', String(vbWidth));
      img.setAttribute('height', String(vbHeight));
    }

    const g = svg.querySelector('g[id^="eon-grid-"]');
    const id = g ? g.getAttribute('id') : null;
    if (!id) return;

    const cells = createGridCells(id);
    if (cells) cellSets.push(cells);
  });

  return cellSets;
}

/**
 * RandomGrid: 1枚分のマスクを開く
 *
 * @param {SVGRectElement[]} cells
 * @returns {gsap.core.Timeline}
 */
function openRandomGrid(cells) {
  const shuffled = gsap.utils.shuffle([...cells]);
  return gsap.timeline().to(shuffled, {
    opacity: 1,
    duration: 1.0,
    ease: 'power3.out',
    stagger: { each: EON_GRID_STAGGER_EACH },
  });
}

/**
 * RandomGrid: テキスト in/out
 *
 * @param {HTMLElement} el
 * @returns {gsap.core.Tween}
 */
function textIn(el) {
  return gsap.to(el, {
    clipPath: 'inset(0% 0% 0% 0%)',
    y: 0,
    duration: 2.6,
    ease: 'expo.out',
  });
}

/**
 * @param {HTMLElement} el
 * @returns {gsap.core.Tween}
 */
function textOut(el) {
  return gsap.to(el, {
    clipPath: 'inset(0% 0% 100% 0%)',
    y: 0,
    duration: 2.0,
    ease: 'power2.inOut',
  });
}

/**
 * RandomGrid: トランジション初期化
 */
function initRandomGridTransition() {
  const stage = document.querySelector('.eon-transition-stage');
  if (!stage) return;

  const showMarkers = shouldShowScrollMarkers();
  const markersLimit = showMarkers ? getScrollMarkersLimit() : 0;
  // セクション突入＝「上端が画面上端に一致」を基準にする（同時発火を避ける）
  const slideStartPosition = 'top bottom';
  const firstSlideStartOffsetPx = 100;
  const markerIndentBase = 24;
  const markerIndentStep = 14;
  const markerPalette = [
    { start: '#3b82f6', end: '#f97316' }, // blue / orange
    { start: '#22c55e', end: '#ef4444' }, // green / red
    { start: '#a855f7', end: '#14b8a6' }, // purple / teal
    { start: '#eab308', end: '#6366f1' }, // yellow / indigo
    { start: '#f43f5e', end: '#10b981' }, // rose / emerald
    { start: '#0ea5e9', end: '#fb7185' }, // sky / pink
  ];

  const texts = gsap.utils.toArray('.eon-transition-text');
  /** @type {ScrollTrigger[]} */
  let slideTriggers = [];

  /**
   * アクティブスライドを更新（フォーカス可能領域のスクロール制御用）
   *
   * @param {HTMLElement} current
   * @returns {void}
   */
  function setActiveSlide(current) {
    const all = document.querySelectorAll('.eon-transition-text');
    all.forEach((el) => el.classList.remove('is-active'));
    current.classList.add('is-active');
  }

  /**
   * スライドごとの ScrollTrigger を構築し、150vh 区間で背景・テキストを同期する
   *
   * @returns {void}
   */
  function buildSlideTriggers() {
    slideTriggers.forEach((t) => t.kill());
    slideTriggers = [];

    const cellSets = buildTransitionMasks();

    texts.forEach((textEl, i) => {
      const cells = cellSets[i];
      if (!cells) return;

      const palette = markerPalette[i % markerPalette.length];
      const markerIndent = markerIndentBase + markerIndentStep * (i % 12);
      const tl = gsap.timeline({ paused: true });
      tl.add(openRandomGrid(cells), 0);
      tl.add(textIn(textEl), 0.05);

      const isFirstSlide = i === 0;

      const st = ScrollTrigger.create({
        trigger: textEl,
        // 1枚目は「スクロールしてから発火」を確実にするため、trigger の上端にオフセットを加算する
        // （`top top+=200` は初期位置で判定が進んでしまうケースがあるため避ける）
        start: isFirstSlide
          ? `top+=${firstSlideStartOffsetPx} top`
          : slideStartPosition,
        end: 'bottom top',
        invalidateOnRefresh: true,
        fastScrollEnd: true,
        markers: showMarkers && i < markersLimit
          ? {
              id: `slide-${i + 1}`,
              startColor: palette.start,
              endColor: palette.end,
              fontSize: '12px',
              indent: markerIndent,
            }
          : false,
        onEnter: () => {
          setActiveSlide(textEl);
          tl.restart(true);
        },
        onLeave: () => {
          // 次のセクションへ抜ける際に中途半端な状態を残さない
          tl.progress(1).pause();
        },
        onEnterBack: () => {
          setActiveSlide(textEl);
          // 上方向へ戻るときに再生し直すとチラつきや連続発火になりやすい。
          // 1枚目は「ロゴのみ」の初期状態に戻す（背景を出さない）。
          if (isFirstSlide) {
            tl.pause(0);
            return;
          }
          // 2枚目以降は「表示状態」へスナップして、背景・テキストを保持する
          tl.progress(1).pause();
        },
        onLeaveBack: () => {
          // 上方向へ抜けたら初期状態へ戻す（再度下へ入った時に1回だけ綺麗に発火させる）
          tl.pause(0);
        },
      });

      slideTriggers.push(st);
    });
  }

  buildSlideTriggers();

  const progressFills = gsap.utils.toArray('.eon-transition-progress__fill');
  ScrollTrigger.create({
    trigger: stage,
    start: 'top top',
    end: 'bottom bottom',
    scrub: 0.3,
    markers: showMarkers
      ? {
          id: 'stage-progress',
          startColor: '#0f0',
          endColor: '#f00',
          fontSize: '12px',
          indent: 0,
        }
      : false,
    onUpdate: (self) => {
      const progress = self.progress;
      const totalSteps = progressFills.length || 1;
      progressFills.forEach((fill, i) => {
        let p = (progress - i / totalSteps) * totalSteps;
        p = Math.max(0, Math.min(1, p));
        fill.style.width = `${p * 100}%`;
      });
    },
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      buildSlideTriggers();
      ScrollTrigger.refresh();
    }, 250);
  });
}

/**
 * Gooey Cursor を有効にしてよい環境か（マウス等のファイン入力かつ hover 可能）
 *
 * @returns {boolean}
 */
function shouldEnableGooeyCursor() {
  return window.matchMedia('(pointer: fine) and (hover: hover)').matches;
}

/**
 * 背景グリッチ演出を有効にしてよい環境か
 *
 * 仕様:
 * - 大面積へのフィルタ/変形を伴うため、タッチ主体端末では無効化する
 *
 * @returns {boolean}
 */
function shouldEnableTransitionGlitch() {
  return window.matchMedia('(pointer: fine) and (hover: hover)').matches;
}

/**
 * Gooey Cursor 初期化（demo/GooeyCursor-main index.html 相当）
 *
 * スマートフォン等のタッチ主体端末ではマウス追従の意味がなく、
 * `cursor.js` の動的 import も行わない。
 */
async function initGooeyCursor() {
  if (!shouldEnableGooeyCursor()) {
    return;
  }

  const cursorEl = document.querySelector('.cursor');
  if (!cursorEl) return;

  const [{ GooCursor }] = await Promise.all([
    import('./gooey-cursor/cursor.js'),
  ]);

  // eslint-disable-next-line no-new
  new GooCursor(cursorEl);
}

/**
 * eon-transition-layer の背景に一定間隔でグリッチを発火させる
 *
 * 仕様:
 * - 一定間隔（ランダム）で短時間だけ .is-glitching を付与
 * - SVG の <filter>（eon-glitch-filter-N）の displacement scale をランダムに変更
 * - prefers-reduced-motion: reduce の場合は何もしない
 *
 * 制限事項:
 * - SVGの疑似要素が使えないため、SVGフィルタ + transform で表現する
 */
function initTransitionLayerGlitch() {
  const stage = document.querySelector('.eon-transition-stage');
  if (!stage) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  if (!shouldEnableTransitionGlitch()) return;

  const layersContainer = document.querySelector('.eon-transition-layers');
  if (!(layersContainer instanceof HTMLElement)) return;

  /**
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  function randInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const glitch = PowerGlitch.glitch(layersContainer, {
    playMode: 'click',
    createContainers: false,
    optimizeSeo: true,
    hideOverflow: true,
    timing: {
      duration: randInt(EON_GLITCH_BURST_MS_MIN, EON_GLITCH_BURST_MS_MAX),
      iterations: 1,
    },
    glitchTimeSpan: { start: 0, end: 1 },
    shake: { velocity: 18, amplitudeX: 0.12, amplitudeY: 0.04 },
    slice: {
      count: 10,
      velocity: 18,
      minHeight: 0.01,
      maxHeight: 0.12,
      hueRotate: true,
      cssFilters: 'contrast(1.15) saturate(1.15)',
    },
    pulse: false,
  });

  let timerId = 0;

  function scheduleNext() {
    const delay = randInt(EON_GLITCH_INTERVAL_MS_MIN, EON_GLITCH_INTERVAL_MS_MAX);
    timerId = window.setTimeout(fireBurst, delay);
  }

  function fireBurst() {
    const duration = randInt(EON_GLITCH_BURST_MS_MIN, EON_GLITCH_BURST_MS_MAX);
    glitch.stopGlitch();
    glitch.startGlitch();
    window.setTimeout(() => glitch.stopGlitch(), duration + 20);

    scheduleNext();
  }

  scheduleNext();

  window.addEventListener(
    'beforeunload',
    () => {
      if (timerId) window.clearTimeout(timerId);
    },
    { once: true }
  );
}

/**
 * eon トップページ用の初期化（上記を一括実行）
 *
 * 制限事項:
 * - `eon-automated-view`（Google 検査・クロール向け）のときは演出を初期化しない。
 */
export function initEonPage() {
  if (document.body.classList.contains('eon-automated-view')) {
    return;
  }

  document.body.classList.add('eon-scroll-stage-ready');

  initLenis();
  initRandomGridTransition();
  initGooeyCursor();
  initTransitionLayerGlitch();
}
